import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { GuestRoute, ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RequireFeature, RequireSuperAdmin } from "@/components/auth/RoleRoute";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { RouteFallback } from "@/shared/components/PageLoader";

const HomePage = lazy(() => import("@/pages/HomePage"));
const LoginPage = lazy(() => import("@/pages/admin/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/admin/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/admin/ForgotPasswordPage"));
const BlogListPage = lazy(() => import("@/pages/blog/BlogListPage"));
const BlogPostPage = lazy(() => import("@/pages/blog/BlogPostPage"));

const DashboardPage = lazy(() => import("@/pages/admin/dashboard/DashboardPage"));
const PostsPage = lazy(() => import("@/pages/admin/dashboard/PostsPage"));
const NewPostPage = lazy(() => import("@/pages/admin/dashboard/NewPostPage"));
const EditPostPage = lazy(() => import("@/pages/admin/dashboard/EditPostPage"));
const ProfilePage = lazy(() => import("@/pages/admin/dashboard/ProfilePage"));
const AdminsPage = lazy(() => import("@/pages/admin/dashboard/AdminsPage"));
const BlogReviewPage = lazy(() => import("@/pages/admin/dashboard/BlogReviewPage"));
const UsersPage = lazy(() => import("@/pages/admin/dashboard/UsersPage"));
const JobsPage = lazy(() => import("@/pages/admin/dashboard/JobsPage"));
const ReferralsPage = lazy(() => import("@/pages/admin/dashboard/ReferralsPage"));
const WaitlistPage = lazy(() => import("@/pages/admin/dashboard/WaitlistPage"));
const SettingsPage = lazy(() => import("@/pages/admin/dashboard/SettingsPage"));
const AuditPage = lazy(() => import("@/pages/admin/dashboard/AuditPage"));
const FeaturesPage = lazy(() => import("@/pages/admin/dashboard/FeaturesPage"));
const DashboardFeaturesPage = lazy(() => import("@/pages/admin/dashboard/DashboardFeaturesPage"));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

function renderDashboardRoutes(basePath: "/admin/dashboard" | "/super_admin/dashboard") {
  return (
    <>
      <Route
        path={basePath}
        element={
          <Lazy>
            <DashboardPage />
          </Lazy>
        }
      />
      <Route
        path={`${basePath}/posts`}
        element={
          <Lazy>
            <PostsPage />
          </Lazy>
        }
      />
      <Route
        path={`${basePath}/posts/new`}
        element={
          <Lazy>
            <NewPostPage />
          </Lazy>
        }
      />
      <Route
        path={`${basePath}/posts/:id/edit`}
        element={
          <Lazy>
            <EditPostPage />
          </Lazy>
        }
      />
      <Route
        path={`${basePath}/profile`}
        element={
          <Lazy>
            <ProfilePage />
          </Lazy>
        }
      />

      <Route element={<RequireFeature featureKey="admins" />}>
        <Route
          path={`${basePath}/admins`}
          element={
            <Lazy>
              <AdminsPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="blog_review" />}>
        <Route
          path={`${basePath}/blog-review`}
          element={
            <Lazy>
              <BlogReviewPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="users" />}>
        <Route
          path={`${basePath}/users`}
          element={
            <Lazy>
              <UsersPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="jobs" />}>
        <Route
          path={`${basePath}/jobs`}
          element={
            <Lazy>
              <JobsPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="referrals" />}>
        <Route
          path={`${basePath}/referrals`}
          element={
            <Lazy>
              <ReferralsPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="waitlist" />}>
        <Route
          path={`${basePath}/waitlist`}
          element={
            <Lazy>
              <WaitlistPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="settings" />}>
        <Route
          path={`${basePath}/settings`}
          element={
            <Lazy>
              <SettingsPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireFeature featureKey="audit" />}>
        <Route
          path={`${basePath}/audit`}
          element={
            <Lazy>
              <AuditPage />
            </Lazy>
          }
        />
      </Route>
      <Route element={<RequireSuperAdmin />}>
        <Route
          path={`${basePath}/features`}
          element={
            <Lazy>
              <FeaturesPage />
            </Lazy>
          }
        />
        <Route
          path={`${basePath}/dashboard-features`}
          element={
            <Lazy>
              <DashboardFeaturesPage />
            </Lazy>
          }
        />
      </Route>
    </>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Lazy>
            <HomePage />
          </Lazy>
        }
      />
      <Route
        path="/blog"
        element={
          <Lazy>
            <BlogListPage />
          </Lazy>
        }
      />
      <Route
        path="/blog/:slug"
        element={
          <Lazy>
            <BlogPostPage />
          </Lazy>
        }
      />

      <Route element={<GuestRoute />}>
        <Route
          path="/admin/login"
          element={
            <Lazy>
              <LoginPage />
            </Lazy>
          }
        />
        <Route
          path="/admin/register"
          element={
            <Lazy>
              <RegisterPage />
            </Lazy>
          }
        />
        <Route
          path="/admin/forgot-password"
          element={
            <Lazy>
              <ForgotPasswordPage />
            </Lazy>
          }
        />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          {renderDashboardRoutes("/admin/dashboard")}
          {renderDashboardRoutes("/super_admin/dashboard")}
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
